
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Tables from "./Tables";

const BASE_URL = process.env.REACT_APP_API_URL;
const ENDPOINT = "/dq-hollistic/rule-details";

const PAGE_SIZE = 80;

const RuleDetails = ({ filters, onFilterOptionsChange, onActiveSearchParamsChange }) => {
  const { parentDomain, domain, subDomain, product, startDate, endDate } = filters;

  const [accumulatedRows, setAccumulatedRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [ruleCount, setRuleCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pageRef = useRef(1);

  const [sortBy, setsortBy] = useState(null);
  const [sortTableBy, setsortTableBy] = useState(null);
  const [activeSearchParams, setActiveSearchParams] = useState({});
  const [columnSearch, setColumnSearch] = useState({
    rule_name: "",
    rule_description: "",
    dimension_name: "",
    check_name: "",
    data_asset_name: "",
    data_product_name: "",
    total_row_count: "",
    error_record_count: "",
    quality_score: "",
    execution_date: "",
    status: "",
  });

  const fetchData = async (append = false) => {
    if (!append) setLoading(true);
    setError(null);

    try {
      const res = await axios.get(`${BASE_URL}${ENDPOINT}`, {
        params: {
          parentDomain: Array.isArray(parentDomain) && parentDomain.length > 0 ? parentDomain.join(",") : "",
          domain: Array.isArray(domain) && domain.length > 0 ? domain.join(",") : "",
          subDomain: Array.isArray(subDomain) && subDomain.length > 0 ? subDomain.join(",") : "",
          product: Array.isArray(product) && product.length > 0 ? product.join(",") : "",
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          page: pageRef.current,
          pageSize: PAGE_SIZE,
          sortBy,
          sortOrder: sortTableBy,
          ...activeSearchParams,
        },
      });

      const newRows = res.data.data || [];
      const pag = res.data.pagination || {};

      if (append) {
        setAccumulatedRows((prev) => [...prev, ...newRows]);
      } else {
        setAccumulatedRows(newRows);
      }

      setTotalRecords(pag.totalCount || 0);
      setRuleCount(res.data.ruleCount || 0);
      setHasMore((pag.page || 1) < (pag.totalPages || 1));
    } catch (err) {
      setError("Failed to fetch rule details");
    } finally {
      if (!append) setLoading(false);
    }
  };

  const loadMoreRules = async () => {
    if (isFetchingMore || !hasMore) return;
    setIsFetchingMore(true);
    try {
      pageRef.current += 1;
      await fetchData(true);
    } finally {
      setIsFetchingMore(false);
    }
  };

  const handleColumnSearch = (key, value) => {
    setColumnSearch((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearchSubmit = (key, value) => {
    setActiveSearchParams((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setColumnSearch({
      rule_name: "",
      rule_description: "",
      dimension_name: "",
      check_name: "",
      data_asset_name: "",
      data_product_name: "",
      total_row_count: "",
      error_record_count: "",
      quality_score: "",
      execution_date: "",
      status: "",
    });
    setActiveSearchParams({});
  };

  useEffect(() => {
    pageRef.current = 1;
    fetchData(false);
  }, [parentDomain, domain, subDomain, product, sortBy, sortTableBy, activeSearchParams, startDate, endDate]);

  useEffect(() => {
    onActiveSearchParamsChange?.(activeSearchParams);
  }, [activeSearchParams]);

  return (
    <div>
      <div className="pl-4 pr-4">
        <Tables
          tableData={accumulatedRows}
          loading={loading}
          error={error}
          totalRecords={totalRecords}
          ruleCount={ruleCount}
          loadMore={loadMoreRules}
          hasMore={hasMore}
          isFetchingMore={isFetchingMore}
          onColumnSearchChange={handleColumnSearch}
          columnSearch={columnSearch}
          onSearchSubmit={handleSearchSubmit}
          onReset={handleReset}
          onSortChange={(column, order) => {
            setsortBy(column);
            setsortTableBy(order);
          }}
        />
      </div>
    </div>
  );
};

export default RuleDetails;
